import { ErrorMessage } from 'aa_common/front-end/antd'
import { If } from 'aa_common/front-end/components'
import InputNumber from 'aa_common/front-end/components/form/form-input-number'
import { everyTrue } from 'aa_common/front-end/helpers'
import { Col, Row } from 'antd'
import { DEPRECIATION_METHOD_CODE } from 'common/constants'
import { getDMethod } from 'common/helpers'
import { useNextEffect } from 'common/hooks/use-next-effect'
import { useSetDefaultFormValue } from 'common/hooks/use-set-default-form-value'
import { getNs } from 'common/i18n-config'
import { LedgerSettingsItem } from 'common/models'
import Form from 'components/molecules/Form'
import { useFormContext } from 'react-hook-form'

import { getUsagePeriodMonthsForImpairment } from '../helper'
import { useGetAssetLedger } from '../hooks/use-get-asset-ledger'
import { useIsFullImpairment } from '../hooks/use-is-full-impairment'
import { useResidualUsagePeriodDefault } from '../hooks/use-residual-usage-period-default'
import { SmallInputClass } from '../styles'
import { FIELD_NAMES } from '../validation'

const { t, useNsReady } = getNs(['components/organisms/assets/asset-impairment', 'common'])
const { FormField } = Form

const FieldName = FIELD_NAMES.impair_usage_period

type ResidualUsagePeriodProps = {
  ledgerSetting?: LedgerSettingsItem
}

export const ResidualUsagePeriod = ({ ledgerSetting }: ResidualUsagePeriodProps) => {
  useNsReady()
  const { setValue } = useFormContext()

  const assetLedger = useGetAssetLedger(ledgerSetting)
  const dMethodCode = getDMethod(assetLedger, true)
  const shouldShow = Boolean(
    dMethodCode &&
    [
      DEPRECIATION_METHOD_CODE.AVERAGE_USAGE_PERIOD,
      DEPRECIATION_METHOD_CODE.LEASE_PERIOD_STRAIGHT_LINE,
      DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD,
    ].includes(dMethodCode)
  )
  const { isFullImpairment: disabled } = useIsFullImpairment()
  const usagePeriodMonthsImpair = getUsagePeriodMonthsForImpairment(assetLedger)
  const defaultValue = useResidualUsagePeriodDefault(usagePeriodMonthsImpair)

  useNextEffect(() => {
    if (dMethodCode === DEPRECIATION_METHOD_CODE.BULK_EQUAL_INSTALLMENT_METHOD) {
      setValue(FIELD_NAMES.impair_usage_period_maximum, 36)
    } else {
      setValue(FIELD_NAMES.impair_usage_period_maximum, usagePeriodMonthsImpair)
    }
  }, [setValue, usagePeriodMonthsImpair, dMethodCode])
  useNextEffect(() => {
    !shouldShow &&
      setValue(FieldName, null, {
        shouldValidate: true,
      })
  }, [setValue, shouldShow])
  useNextEffect(() => {
    shouldShow &&
      !disabled &&
      setValue(FieldName, defaultValue, {
        shouldValidate: true,
      })
    shouldShow &&
      disabled &&
      setValue(FieldName, null, {
        shouldValidate: true,
      })
  }, [setValue, shouldShow, disabled, defaultValue])

  const setDefaultValue = useSetDefaultFormValue(FieldName, 1, everyTrue(!disabled, shouldShow))

  return (
    <If condition={shouldShow}>
      <FormField title={t('residual_usage_period')} required={!disabled} disabled={disabled}>
        <If condition={disabled}>
          <span className="disabled-content">ー</span>
        </If>
        <If condition={!disabled}>
          <Row align="middle" gutter={8}>
            <Col>
              <InputNumber
                min={1}
                name={FieldName}
                className={SmallInputClass}
                controls={false}
                isShowError={false}
                onBlur={setDefaultValue}
              />
            </Col>
            <Col>
              <span>{t('residual_usage_period_unit')}</span>
            </Col>
          </Row>
        </If>
        <ErrorMessage name={FieldName} errors={null} />
      </FormField>
    </If>
  )
}
